import { useEffect, useState, useRef } from 'react';
import { adminApi } from '../api/admin.js';

/**
 * <AuthenticatedImage screenshotId="…" /> — renders a screenshot whose URL
 * requires a bearer token. We can't pass the token via the `src` attribute
 * of a plain <img>, so we fetch the bytes via the api client (which adds
 * the Authorization header + handles silent refresh), turn the response
 * into a Blob URL, and assign that to <img src>.
 *
 * The Blob URL is revoked on unmount so the browser can release the bytes.
 */
export function AuthenticatedImage({ screenshotId, alt, className, style, onClick }) {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(null);
    const urlRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setSrc(null);
        setErr(null);

        adminApi.fetchScreenshotBlob(screenshotId)
            .then((blob) => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                urlRef.current = url;
                setSrc(url);
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(e.message || 'Could not load image');
            });

        return () => {
            cancelled = true;
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = null;
            }
        };
    }, [screenshotId]);

    if (err) {
        return (
            <div
                className={className}
                style={{
                    ...style,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-tertiary)',
                    fontSize: 11,
                    border: '0.5px dashed var(--color-border-tertiary)',
                }}
            >
                broken
            </div>
        );
    }
    if (!src) {
        return (
            <div
                className={className}
                style={{
                    ...style,
                    background: 'var(--color-bg-secondary)',
                }}
            />
        );
    }
    return (
        <img
            src={src}
            alt={alt || ''}
            className={className}
            style={style}
            onClick={onClick}
            draggable={false}
        />
    );
}
